import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail, money, API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CategorySelect } from "@/components/CategorySelect";
import { Stack, Plus, PencilSimple, Trash, Check, X, DownloadSimple, FilePdf, Image as ImageIcon, UploadSimple, ArrowsLeftRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Settings() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newGroup, setNewGroup] = useState("Hard Costs");
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [removeTarget, setRemoveTarget] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [hasLogo, setHasLogo] = useState(false);
  const [logoBust, setLogoBust] = useState(Date.now());
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [unmapped, setUnmapped] = useState([]);
  const [mapChoices, setMapChoices] = useState({});
  const [remapping, setRemapping] = useState(false);
  const { user } = useAuth();
  const isManager = ["owner", "admin"].includes(user?.role);
  const navigate = useNavigate();

  const loadUnmapped = () => {
    api.get("/categories/unmapped").then((r) => {
      const list = r.data.unmapped || [];
      setUnmapped(list);
      setMapChoices((prev) => {
        const next = { ...prev };
        for (const u of list) if (!next[u.name] && u.suggestion) next[u.name] = u.suggestion;
        return next;
      });
    }).catch(() => {});
  };

  const load = () => {
    setLoading(true);
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {}).finally(() => setLoading(false));
    loadUnmapped();
  };
  useEffect(() => {
    load();
    api.get("/team/branding").then((r) => setHasLogo(r.data.has_logo)).catch(() => {});
  }, []);

  const applyRemap = async () => {
    const mappings = unmapped
      .filter((u) => mapChoices[u.name])
      .map((u) => ({ from_category: u.name, to_category: mapChoices[u.name] }));
    if (!mappings.length) { toast.error("Pick a category for at least one item"); return; }
    setRemapping(true);
    try {
      const { data } = await api.post("/categories/remap", { mappings });
      toast.success(`Remapped ${data.updated} cost item${data.updated === 1 ? "" : "s"}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setRemapping(false); }
  };

  const onLogoSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/team/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded — it'll appear on your PDF reports");
      setHasLogo(true);
      setLogoBust(Date.now());
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setUploadingLogo(false); e.target.value = ""; }
  };

  const removeLogo = async () => {
    try {
      await api.delete("/team/branding/logo");
      setHasLogo(false);
      toast.success("Logo removed");
    } catch (err) { toast.error("Could not remove logo"); }
  };

  const addCategory = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await api.post("/categories", { name: newName.trim(), group: newGroup === "Other" ? "" : newGroup });
      toast.success(`Added "${newName.trim()}"`);
      setNewName("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setAdding(false); }
  };

  const startEdit = (c) => { setEditId(c.id); setEditName(c.name); };
  const cancelEdit = () => { setEditId(null); setEditName(""); };

  const saveEdit = async (c) => {
    if (!editName.trim() || editName.trim() === c.name) { cancelEdit(); return; }
    try {
      await api.put(`/categories/${c.id}`, { name: editName.trim() });
      toast.success("Category renamed — existing cost items updated");
      cancelEdit();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeCategory = async () => {
    if (!removeTarget) return;
    try {
      await api.delete(`/categories/${removeTarget.id}`);
      toast.success(`Removed "${removeTarget.name}"`);
      setRemoveTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openDetail = async (c) => {
    setDetail({ id: c.id, category: c.name, items: [] });
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/categories/${c.id}/line-items`);
      setDetail({ ...data, id: c.id });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setDetail(null);
    } finally { setDetailLoading(false); }
  };

  const downloadExport = async (fmt) => {
    if (!detail?.id) return;
    try {
      const res = await api.get(`/categories/${detail.id}/export.${fmt}`, { responseType: "blob" });
      const mime = fmt === "pdf" ? "application/pdf" : "text/csv";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${detail.category.replace(/ /g, "_")}_cost_report.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error("Could not download report"); }
  };

  return (
    <div>
      <PageHeader label="Configuration" title="Settings" />
      <div className="p-4 sm:p-8 space-y-8">
        {isManager && (
          <div className="border border-border" data-testid="company-branding-section">
            <div className="px-4 py-3 border-b border-border bg-secondary flex items-center gap-2">
              <ImageIcon size={16} /><span className="font-heading font-bold">Company Branding</span>
            </div>
            <div className="p-6 space-y-4 max-w-2xl">
              <p className="text-sm text-muted-foreground">
                Upload your company logo. It appears at the top of your PDF cost reports (category & portfolio),
                alongside the preparer's name and date — so exports look client-ready for lenders and partners.
              </p>
              <div className="flex items-center gap-5">
                <div className="h-20 w-40 border border-border bg-secondary/40 flex items-center justify-center overflow-hidden">
                  {hasLogo ? (
                    <img src={`${API}/team/branding/logo?t=${logoBust}`} alt="Company logo" data-testid="company-logo-preview" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <span className="text-xs text-muted-foreground">No logo</span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="inline-flex items-center gap-2 px-4 py-2 border border-primary bg-primary text-primary-foreground text-sm cursor-pointer hover:opacity-90 transition-opacity duration-200" data-testid="upload-logo-label">
                    <UploadSimple size={16} /> {uploadingLogo ? "Uploading…" : hasLogo ? "Replace logo" : "Upload logo"}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onLogoSelected} disabled={uploadingLogo} className="hidden" data-testid="upload-logo-input" />
                  </label>
                  {hasLogo && (
                    <button onClick={removeLogo} data-testid="remove-logo-button" className="text-xs text-muted-foreground hover:text-destructive text-left transition-colors duration-200">Remove logo</button>
                  )}
                  <span className="text-xs text-muted-foreground">PNG, JPG, WEBP or GIF · max 5MB</span>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="border border-border" data-testid="cost-categories-section">
          <div className="px-4 py-3 border-b border-border bg-secondary flex items-center gap-2">
            <Stack size={16} /><span className="font-heading font-bold">Cost Categories</span>
          </div>
          <div className="p-6 space-y-6 max-w-2xl">
            <p className="text-sm text-muted-foreground">
              Your team uses a structured taxonomy split into <strong>Hard Costs</strong> (tangible assets) and
              <strong> Soft Costs</strong> (intangible services). Renaming a category also updates it on all existing
              cost items, quotations, and Cost Library entries. Any team member can manage these.
            </p>

            <form onSubmit={addCategory} className="flex flex-col sm:flex-row gap-2" data-testid="add-category-form">
              <Select value={newGroup} onValueChange={setNewGroup}>
                <SelectTrigger className="rounded-none sm:w-44" data-testid="new-category-group"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Hard Costs">Hard Costs</SelectItem>
                  <SelectItem value="Soft Costs">Soft Costs</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New category name"
                data-testid="new-category-input" className="rounded-none flex-1" />
              <Button type="submit" disabled={adding || !newName.trim()} className="rounded-none shrink-0" data-testid="add-category-button">
                <Plus size={16} className="mr-1" /> Add
              </Button>
            </form>

            {loading ? (
              <p className="text-sm text-muted-foreground animate-pulse">Loading…</p>
            ) : cats.length === 0 ? (
              <p className="text-sm text-muted-foreground">No categories yet. Add one above.</p>
            ) : (
              <div className="space-y-5" data-testid="categories-list">
                {["Hard Costs", "Soft Costs", "Other"].map((grp) => {
                  const groupCats = cats.filter((c) => (c.group || "Other") === grp);
                  if (!groupCats.length) return null;
                  return (
                    <div key={grp} data-testid={`category-group-${grp.replace(/ /g, "-").toLowerCase()}`}>
                      <div className="label-caps mb-2">{grp}</div>
                      <div className="border border-border divide-y divide-border">
                        {groupCats.map((c) => (
                          <div key={c.id} data-testid={`category-row-${c.id}`} className="flex items-center gap-3 px-4 py-2.5">
                            {editId === c.id ? (
                              <>
                                <Input value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus
                                  onKeyDown={(e) => { if (e.key === "Enter") saveEdit(c); if (e.key === "Escape") cancelEdit(); }}
                                  data-testid={`edit-category-input-${c.id}`} className="rounded-none h-8 flex-1" />
                                <button onClick={() => saveEdit(c)} data-testid={`save-category-${c.id}`} aria-label="Save"
                                  className="text-emerald-700 hover:text-emerald-800"><Check size={18} weight="bold" /></button>
                                <button onClick={cancelEdit} aria-label="Cancel" className="text-muted-foreground hover:text-primary"><X size={18} weight="bold" /></button>
                              </>
                            ) : (
                              <>
                                <div className="flex-1 min-w-0">
                                  <button onClick={() => openDetail(c)} data-testid={`view-category-${c.id}`}
                                    className="text-sm font-medium hover:text-primary hover:underline text-left transition-colors duration-200">
                                    {c.name}
                                  </button>
                                  {c.description && (
                                    <span className="block text-xs text-muted-foreground/80 mt-0.5" data-testid={`category-desc-${c.id}`}>{c.description}</span>
                                  )}
                                  {c.budget_pct && (
                                    <span className="inline-block text-[10px] mt-1 px-1.5 py-0.5 bg-secondary text-muted-foreground font-mono-fig" data-testid={`category-pct-${c.id}`}>Typical: {c.budget_pct}</span>
                                  )}
                                  {c.item_count > 0 && (
                                    <span className="block text-xs text-muted-foreground mt-0.5" data-testid={`category-stats-${c.id}`}>
                                      {c.item_count} item{c.item_count === 1 ? "" : "s"} · <span className="font-mono-fig">{money(c.total_budget)}</span> budgeted · <span className="font-mono-fig">{money(c.total_actual)}</span> actual
                                    </span>
                                  )}
                                </div>
                                <button onClick={() => startEdit(c)} data-testid={`rename-category-${c.id}`} aria-label="Rename"
                                  className="text-muted-foreground hover:text-primary transition-colors duration-200"><PencilSimple size={16} /></button>
                                <button onClick={() => setRemoveTarget(c)} data-testid={`delete-category-${c.id}`} aria-label="Delete"
                                  className="text-muted-foreground hover:text-destructive transition-colors duration-200"><Trash size={16} /></button>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {unmapped.length > 0 && (
          <div className="border border-amber-400" data-testid="remap-categories-section">
            <div className="px-4 py-3 border-b border-amber-400 bg-amber-50 flex items-center gap-2">
              <ArrowsLeftRight size={16} className="text-amber-700" />
              <span className="font-heading font-bold text-amber-900">Unmapped Cost Categories</span>
              <span className="ml-auto text-xs font-mono-fig bg-amber-200 text-amber-900 px-2 py-0.5">{unmapped.length}</span>
            </div>
            <div className="p-6 space-y-5 max-w-3xl">
              <p className="text-sm text-muted-foreground">
                These category names on your existing cost items, quotations, and Cost Library entries don't match your
                current taxonomy. We've pre-selected a best-guess mapping — review each, adjust as needed, then apply to
                remap all affected records at once.
              </p>
              <div className="border border-border divide-y divide-border">
                {unmapped.map((u) => (
                  <div key={u.name} className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3" data-testid={`unmapped-row-${u.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium">{u.name}</span>
                      <span className="block text-xs text-muted-foreground">{u.count} record{u.count === 1 ? "" : "s"}</span>
                    </div>
                    <ArrowsLeftRight size={16} className="text-muted-foreground hidden sm:block shrink-0" />
                    <div className="sm:w-72">
                      <CategorySelect
                        categories={cats}
                        value={mapChoices[u.name] || ""}
                        onChange={(v) => setMapChoices((p) => ({ ...p, [u.name]: v }))}
                        placeholder="Map to…"
                        className="rounded-none"
                        testId={`map-select-${u.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <Button onClick={applyRemap} disabled={remapping} className="rounded-none" data-testid="apply-remap-button">
                <Check size={16} className="mr-1" /> {remapping ? "Applying…" : "Apply mapping"}
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-none max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="category-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{detail?.category} — cost items</DialogTitle>
            <DialogDescription>Every cost item in this category across your active projects. Click a row to open its project.</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <p className="text-sm text-muted-foreground animate-pulse py-6">Loading…</p>
          ) : (detail?.items?.length || 0) === 0 ? (
            <p className="text-sm text-muted-foreground py-6">No cost items in this category yet.</p>
          ) : (
            <>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => downloadExport("csv")} className="rounded-none" data-testid="export-category-csv">
                  <DownloadSimple size={15} className="mr-1" /> CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadExport("pdf")} className="rounded-none" data-testid="export-category-pdf">
                  <FilePdf size={15} className="mr-1" /> PDF
                </Button>
              </div>
              <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Project</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-right">Budget</TableHead>
                  <TableHead className="text-right">Actual</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detail.items.map((it) => (
                  <TableRow key={it.id} data-testid={`category-item-${it.id}`}
                    onClick={() => { setDetail(null); navigate(`/projects/${it.project_id}`); }}
                    className="cursor-pointer hover:bg-secondary transition-colors duration-200">
                    <TableCell className="font-medium">{it.project_name}</TableCell>
                    <TableCell className="text-sm">{it.description || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{it.vendor_name || "—"}</TableCell>
                    <TableCell className="text-right font-mono-fig tabular-nums">{money(it.budget_amount)}</TableCell>
                    <TableCell className="text-right font-mono-fig tabular-nums">{money(it.actual_amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <DialogContent className="rounded-none" data-testid="delete-category-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete category?</DialogTitle>
            <DialogDescription>
              {removeTarget && `"${removeTarget.name}" will be removed from your category list. Existing cost items keep their current category text — this only affects the reusable list.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveTarget(null)} className="rounded-none" data-testid="delete-category-cancel">Cancel</Button>
            <Button variant="destructive" onClick={removeCategory} className="rounded-none" data-testid="delete-category-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
